import React from 'react';
import { action, observable, computed, ObservableMap } from 'mobx';
import { observer } from 'mobx-react';
import {
  Stack,
  HStack,
  Select,
  Box,
  Text,
  NumberInput,
  NumberInputField,
} from '@chakra-ui/react';

import { SingleDatepicker, RangeDatepicker } from 'components/Datepicker';

import {
  formatRFC3339,
  subDays,
  subMonths,
  addDays,
  startOfDay,
  isToday,
  differenceInMinutes,
  isPast,
  differenceInDays,
  differenceInMonths,
  isSameDay,
} from 'date-fns';

import {
  FilterField,
  FilterOps,
  FilterInput,
  ValueInputType,
} from '../filterModels';

const DateFilterView = observer(({ filter }) => {
  const predicates = filter.constructor.Predicates;
  const PredicateView = filter.currentPredicate.view;
  return (
    <Stack py={3} px={2} backgroundColor='#fbfbfb'>
      <Select
        colorScheme='blue'
        onChange={e => filter.SwitchPredicate(e.target.value)}
        value={filter.currentPredicateKey}
        borderRadius='0px'
        color='black'
        cursor='pointer'
      >
        {Object.keys(predicates).map((predKey, i) => {
          return (
            <option value={predKey} key={i}>
              {predicates[predKey].title}
            </option>
          );
        })}
      </Select>
      <Box>
        <PredicateView
          filter={filter}
          touched={filter.touchedPredicates.get(filter.predKey)}
        />
      </Box>
    </Stack>
  );
});

const LastPredicateView = observer(({ filter }) => {
  /* In the last X days */
  const changeNumber = num => {
    const predicateState = filter.currentPredicateState;
    if (predicateState === undefined) {
      filter.SetPredicateState({ number: num });
    } else {
      predicateState.number = num;
      filter.SetPredicateState(predicateState);
    }
  };
  const changeUnit = str => {
    const predicateState = filter.currentPredicateState;
    if (predicateState === undefined) {
      filter.SetPredicateState({ unit: str });
    } else {
      predicateState.unit = str;
      filter.SetPredicateState(predicateState);
    }
  };
  return (
    <HStack spacing='24px'>
      <Text>&#8627;</Text>
      <NumberInput
        errorBorderColor='red.500'
        isRequired={true}
        isInvalid={!filter.isValid && (filter.touched || filter.applying)}
        onBlur={() => filter.SetTouched(true)}
        onFocus={() => filter.SetTouched(false)}
        value={filter.currentPredicateState?.number}
        borderRadius='0px'
        color='black'
        size='sm'
        maxW={20}
        min={1}
        max={10000}
        onChange={changeNumber}
      >
        <NumberInputField />
      </NumberInput>
      <Select
        size='sm'
        maxW={20}
        onChange={e => changeUnit(e.target.value)}
        value={filter.currentPredicateState?.unit || 'days'}
        borderRadius='0px'
      >
        <option value='days' key='days'>
          days
        </option>
        <option value='months' key='months'>
          months
        </option>
      </Select>
    </HStack>
  );
});

const DatePredicateView = observer(({ filter }) => {
  const predState = filter.currentPredicateState;
  // What to do about initial Or empty values?
  return (
    <HStack spacing='24px'>
      <Text>&#8627;</Text>
      <SingleDatepicker
        colorScheme='blue'
        name='date-input'
        date={predState}
        isInvalid={!filter.isValid}
        touched={filter.touched}
        SetTouched={filter.SetTouched}
        onDateChange={date => {
          filter.SetPredicateState(date);
        }}
        applying={filter.applying}
        /* const DefaultConfigs = {
          dateFormat: 'yyyy-MM-dd',
          monthNames: MonthNamesShort,
          dayNames: WeekdayNamesShort,
        };    */
      />
    </HStack>
  );
});

const DateRangePredicateView = observer(({ filter }) => {
  const predState = filter.currentPredicateState;
  // What to do about initial or empty values?
  return (
    <HStack spacing='24px'>
      <Text>&#8627;</Text>
      <RangeDatepicker
        colorScheme='blue'
        name='date-input'
        selectedDates={predState}
        isInvalid={!filter.isValid}
        touched={filter.touched}
        SetTouched={filter.SetTouched}
        onDateChange={dates => {
          filter.SetPredicateState(dates);
        }}
        applying={filter.applying}
        /* const DefaultConfigs = {
          dateFormat: 'yyyy-MM-dd',
          monthNames: MonthNamesShort,
          dayNames: WeekdayNamesShort,
        };  */
      />
    </HStack>
  );
});

export class DateFilter {
  static View = DateFilterView;
  static OpJoin = 'AND';
  static Predicates = {
    last: {
      title: 'in the last',
      view: LastPredicateView,
      isValid: state => {
        if (/^\d+$/.test(state.number) && state?.unit) {
          return true;
        }
        return false;
      },
    },
    between: {
      title: 'between',
      view: DateRangePredicateView,
      isValid: state =>
        state?.length === 2 &&
        state[0] instanceof Date &&
        state[1] instanceof Date,
    },
    before: {
      title: 'before',
      view: DatePredicateView,
      isValid: state => state instanceof Date,
    },
    after: {
      title: 'after',
      view: DatePredicateView,
      isValid: state => state instanceof Date,
    },
    onDate: {
      title: 'on date',
      view: DatePredicateView,
      isValid: state => state instanceof Date,
    },
  };

  filterField;

  @observable currentPredicateKey = 'last';

  @observable predicateState = {
    last: { number: '', unit: 'days' },
    // between: [new Date(), new Date()],
  };

  @observable touchedPredicates = new ObservableMap();
  @observable applying = false;

  constructor({ fieldName, fieldTitle, selected, filterInputs }) {
    this.filterField = new FilterField({
      filter: this,
      filterInputs,
      fieldName,
      fieldTitle,
      selected,
    });
    this.predicateStateFromFilterInputs(filterInputs);
  }

  predicateStateFromFilterInputs(filterInputs) {
    if (filterInputs.length === 1) {
      if (filterInputs[0].operator === FilterOps.LTE) {
        this.currentPredicateKey = 'before';
      } else if (filterInputs[0].operator === FilterOps.GTE) {
        this.currentPredicateKey = 'after';
      }
      this.SetPredicateState(filterInputs[0].value.toValue());
    } else if (filterInputs.length === 2) {
      let lte;
      let gte;
      filterInputs.forEach(filterInput => {
        if (filterInput.operator === FilterOps.LTE) {
          lte = filterInput.value.toValue();
        } else if (filterInput.operator === FilterOps.GTE) {
          gte = filterInput.value.toValue();
        }
      });
      // cannot assume dates are ordered when coming from the url
      if (lte && gte) {
        if (differenceInMinutes(lte, gte) === 24 * 60) {
          this.currentPredicateKey = 'onDate';
          this.SetPredicateState(gte);
        } else if (isToday(lte) && isPast(gte) && !isToday(gte)) {
          this.currentPredicateKey = 'last';
          // if duration is an amount of months? if duration is great than a certain amount e.g. a year
          const number = differenceInDays(lte, gte);
          if (number >= 28) {
            const diffMonths = differenceInMonths(lte, gte);
            const exactMonthDate = subMonths(lte, diffMonths);
            if (isSameDay(exactMonthDate, gte)) {
              this.SetPredicateState({ number: diffMonths, unit: 'months' });
              return;
            }
          }
          this.SetPredicateState({ number, unit: 'days' });
        } else {
          this.currentPredicateKey = 'between';
          this.SetPredicateState([gte, lte]);
        }
      } // otherwise its invalid
    }
  }

  SetPredicateState = state => {
    /* Set it from URL on load and otherwise set it locally from the UI */
    this.predicateState[this.currentPredicateKey] = state;
    if (this.currentPredicate.isValid(state)) {
      this.SetFilterInputs(state);
    } else {
      this.filterField.SetFilterInputs([]); // clear FilterInputs from any previously valid predicate
      // set predicate to invalid? and block apply/display invalid
    }
  };

  @computed get isValid() {
    return this.currentPredicate.isValid(this.currentPredicateState);
  }

  @action SwitchPredicate = predicateKey => {
    this.currentPredicateKey = predicateKey;
    this.SetPredicateState(this.currentPredicateState);
  };

  @action SetApplying = applying => {
    this.applying = applying;
  };

  @action SetTouched = isTouched => {
    this.touchedPredicates.set(this.currentPredicateKey, isTouched);
  };

  @computed get touched() {
    return this.touchedPredicates.get(this.currentPredicateKey) || false;
  }

  @computed get currentPredicateState() {
    return this.predicateState[this.currentPredicateKey];
  }

  @computed get currentPredicate() {
    return this.constructor.Predicates[this.currentPredicateKey];
  }

  createDateInput(date, op) {
    return [
      new FilterInput({
        field: this.filterField.name,
        value: formatRFC3339(date),
        type: ValueInputType.DATETIME,
        operator: op,
      }),
    ];
  }

  createDateRangeInput(dates) {
    return [
      new FilterInput({
        field: this.filterField.name,
        value: formatRFC3339(dates[0]),
        type: ValueInputType.DATETIME,
        operator: FilterOps.GTE,
      }),
      new FilterInput({
        field: this.filterField.name,
        value: formatRFC3339(dates[1]),
        type: ValueInputType.DATETIME,
        operator: FilterOps.LTE,
      }),
    ];
  }

  createLastInput = state => {
    /* We interpret, in the last X days to mean, from now to the beginning of the day 10 days ago.
    'state' is {number: X, unit: 'days'} and should have been validated by the time it reaches here */
    const lte = Date.now();
    if (state.unit === 'days') {
      const gte = startOfDay(subDays(lte, state.number));
      return this.createDateRangeInput([gte, lte]);
    }
    if (state.unit === 'months') {
      const gte = startOfDay(subMonths(lte, state.number));
      return this.createDateRangeInput([gte, lte]);
    }
    return [];
  };

  @action SetFilterInputs = state => {
    const { SetFilterInputs } = this.filterField;
    if (this.currentPredicateKey === 'before') {
      SetFilterInputs(this.createDateInput(state, FilterOps.LTE));
    } else if (this.currentPredicateKey === 'after') {
      SetFilterInputs(this.createDateInput(state, FilterOps.GTE));
    } else if (this.currentPredicateKey === 'between') {
      // if (filterInputs.length === 2)
      SetFilterInputs(this.createDateRangeInput(state));
    } else if (this.currentPredicateKey === 'onDate') {
      // TODO: normalise the dates to the beginning and end of the day? provided in filterDates[0]
      SetFilterInputs(this.createDateRangeInput([state, addDays(state, 1)]));
    } else if (this.currentPredicateKey === 'last') {
      // TODO: from now to now + last days/month
      SetFilterInputs(this.createLastInput(state));
    }
  };
}
